import React, { useEffect, useState } from "react"
import { injectIntl, defineMessages, FormattedMessage } from "react-intl"
import { Intl } from "../../../state/helpers/IntlProps"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import EcommerceItems from "./EcommerceItems"
import ActionButton from "../../../common/button/actionbutton.component"
import EcommerceAddFormFieldsModal from "./EcommerceAddFormFieldsModal"
import Icon from "../../../common/icon/icon.component"
import EcommerceConfiguration, {
    CreateEcommerceConfigurationDto
} from "../../../state/ecommerce-configuration/EcommerceConfigurationValues"
import QuoteFormConfiguration, {
    QuoteFormConfigurationDto
} from "../../../state/ecommerce-configuration/EcommerceQuoteFormConfiguration"
import { ECOMMERCE_CONFIGURATION_TYPE } from "../../../state/ecommerce-configuration/EcommerceTypes"

type OwnProps = {
    intl: Intl
    handleUpdateCallback: (config: QuoteFormConfiguration[]) => void
    handleSubmitCallback: (config: CreateEcommerceConfigurationDto) => void
    configuration: EcommerceConfiguration
    configurationType: string
}

const messages = defineMessages({
    "add_form_component_btn": {
        id: "ecommerce.section.quote_form_container.add_form_component.label",
        defaultMessage: "Add A Form Component",
    },
    "submit_btn": {
        id: "ecommerce.section.quote_form_container.submit_button.label",
        defaultMessage: "Save Form Configuration",
    }
})


const EcommerceConfigurationList = (props: OwnProps) => {
    const [showAddModal, setShowAddModal] = useState(false)
    const [fields, setFields] = useState<QuoteFormConfiguration[]>([])

    useEffect(() => {
        if (!props.configuration) {
            return
        }
        const stylemapping = props.configuration.getEcommerceStyleMappingByType(ECOMMERCE_CONFIGURATION_TYPE.QUOTE_FORM)
        const formConfigurations = stylemapping.getFormConfiguration()
        if (formConfigurations) {
            const fieldsFromBackEnd: QuoteFormConfiguration[] = []
            for (const field of formConfigurations) {
                if (field.getAttributes() && field.getAttributes().subFields.length > 0) {
                    const castedSubFields: QuoteFormConfiguration[] = []
                    const subFields: QuoteFormConfiguration[] = field.getSubFields()
                    for (let subField of subFields) {
                        if (!(subField instanceof QuoteFormConfiguration)) {
                            subField = new QuoteFormConfiguration(subField as unknown as QuoteFormConfigurationDto)
                        }
                        castedSubFields.push(subField)
                    }
                    field.setAttributes("subFields", castedSubFields)
                }
                fieldsFromBackEnd.push(field as QuoteFormConfiguration)
            }
            setFields(fieldsFromBackEnd)
            props.handleUpdateCallback(fieldsFromBackEnd)

        }

    }, [props.configuration])

    const handleDeleteField = (name) => {
        const existingFields = [...fields]
        existingFields.forEach((item, index) => {
            if (item.getAttributes().name === name) existingFields.splice(index, 1)
        })

        const reRankedFieldsAfterDeletion = reRankFields(existingFields)
        setFields(reRankedFieldsAfterDeletion)
        props.handleUpdateCallback(reRankedFieldsAfterDeletion)
    }


    const updateField = (name, fieldAttributeToChange, changedToValue, parentField) => {
        const newFields: QuoteFormConfiguration[] = [...fields]

        for (const field of newFields) {
            if (field.getAttributes().name == name) {
                field.setAttributes(fieldAttributeToChange, changedToValue)
                setFields(newFields)
                props.handleUpdateCallback(newFields)
                break
            } else if (field.getAttributes().subFields?.length > 0) {
                for (let subField of field.getAttributes().subFields) {
                    if (!(subField instanceof QuoteFormConfiguration)) {
                        subField = new QuoteFormConfiguration(subField as unknown as QuoteFormConfigurationDto)
                    }
                    if (subField.getAttributes().name == name && field.getAttributes().name == parentField) {
                        subField.setAttributes(fieldAttributeToChange, changedToValue)
                        setFields(newFields)
                        props.handleUpdateCallback(newFields)
                        break
                    }
                }
            }

        }

    }

    const renderConfigurationList = () => {
        const items = fields
        items.sort((a, b) => (a.getAttributes().rank > b.getAttributes().rank ? 1 : -1))
        return [
            items?.map((field, index) => {
                const fieldAttributes = field.getAttributes()
                return <Draggable key={`ecommerceConfig${index}`}
                    draggableId={fieldAttributes.name} index={index}>
                    {(provided) => (<div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                    >
                        <EcommerceItems
                            fieldName={fieldAttributes.name}
                            fieldLabel={fieldAttributes.label}
                            fieldType={fieldAttributes.type}
                            required={fieldAttributes.required}
                            onChangeCallback={updateField}
                            dragHandle={provided.dragHandleProps}
                            handleDelete={handleDeleteField}
                            subFields={fieldAttributes.subFields}
                            isSubField={false}
                            parentField=""
                        />
                    </div>)}
                </Draggable>
            })
        ]
    }


    const onDragEnd = (result) => {
        const { source, destination } = result

        if (!destination) {
            return
        }

        if (source.droppableId === destination.droppableId) {
            const updatedData = reorder(
                fields,
                result.source.index,
                result.destination.index
            )
            const reRankedUpdatedData = reRankFields(updatedData)
            setFields(reRankedUpdatedData)
            props.handleUpdateCallback(fields)
        }

    }

    const reRankFields = (list: QuoteFormConfiguration[]): QuoteFormConfiguration[] => {
        list.map((i, index) => {
            i.getAttributes().rank = index
        })
        return list

    }
    const reorder = (list: QuoteFormConfiguration[], start, end): QuoteFormConfiguration[] => {
        const result = Array.from(list)
        const [removed] = result.splice(start, 1)
        result.splice(end, 0, removed)
        return result as QuoteFormConfiguration[]
    }

    const onDragStart = () => {
        return
    }

    const handleOnSubmitCallback = () => {
        const data = [] as QuoteFormConfiguration[]

        for (const c of fields) {
            const item = c as unknown as QuoteFormConfiguration
            item.setAttributes("subFields", item.getAttributes().subFields as QuoteFormConfiguration[])
            data.push(item)
        }
        let styleMapping
        if (props.configuration) {
            styleMapping = props.configuration.getEcommerceStyleMappingByType(ECOMMERCE_CONFIGURATION_TYPE.QUOTE_FORM)
            styleMapping?.setFormConfiguration(data)
            const payload: CreateEcommerceConfigurationDto = {
                configurationType: props.configurationType,
                settingMappings: [],
                styleMappings: styleMapping.getAttributesAndForm() || Object(),
            }
            props.handleSubmitCallback(payload)
        }
    }

    const handleOnFieldSelectCallback = (field: QuoteFormConfiguration) => {
        const rank = fields.length ? fields.length : 0
        field.setAttributes("rank", rank)
        const updatedFields = [
            ...fields, field
        ]
        const castedFields = updatedFields as QuoteFormConfiguration[]
        setFields(castedFields)
        props.handleUpdateCallback(castedFields)
    }


    return (
        <>
            <div className="col-12 ui-module mt-5">
                <div className="row">
                    <div className="col-12 ui-title-bar mb-3">
                        <div className="row">
                            <div className="col-12 pb-3">
                                <h2>
                                    <FormattedMessage
                                        id="settings.ecommerce.quoteform.form_component.title"
                                        defaultMessage="Edit Your Form Components"
                                    />
                                </h2>
                                <ActionButton onClick={() => {
                                    setShowAddModal(true)
                                }}
                                type="button"
                                className="btn btn-primary float-right"
                                loadingTimeout={1000}
                                >
                                    <Icon className={"mr-1"} value={"plus-square"} />
                                    {props.intl.formatMessage(messages["add_form_component_btn"])}
                                </ActionButton>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                                    <div>
                                        <Droppable droppableId={"ecommerce-configuration-dropable"} isDropDisabled={false}>
                                            {(provided, snapshot) => (
                                                <div
                                                    id={"#sortable-item"}
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={"col-12 group-sortable " +
                                                        (snapshot.isDraggingOver ? "dnd-highlight" : "")}>
                                                    {renderConfigurationList()}
                                                    {provided.placeholder}
                                                </div>
                                            )}

                                        </Droppable>
                                    </div>
                                </DragDropContext>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <ActionButton onClick={handleOnSubmitCallback}
                        type="button"
                        className="btn btn-primary float-right"
                        loadingTimeout={1000}>
                        {props.intl.formatMessage(messages["submit_btn"])}
                    </ActionButton>
                </div>
            </div>
            {
                showAddModal &&
                <EcommerceAddFormFieldsModal
                    onClose={() => {
                        setShowAddModal(false)
                    }}
                    currentFields={fields}
                    onFieldSelectCallBack={handleOnFieldSelectCallback}
                />
            }
        </>
    )

}

export default injectIntl(EcommerceConfigurationList)


// WEBPACK FOOTER //
// ./src/settings/ecommerce/components/EcommerceConfigurationList.tsx